import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import {
  PHOTO_HOLD_ROLE_ICONS,
  PHOTO_HOLD_ROLE_SWATCHES,
  type PhotoHoldRole,
} from './photo-hold-roles';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';

interface HoldRolesLegendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The roles this surface actually offers, in legend order — the board's and the
   * photo's differ over the match (see PHOTO_HOLD_ROLE_CYCLE), and a legend that
   * described a colour the setter cannot paint would be a legend to the wrong
   * wall.
   */
  roles: readonly PhotoHoldRole[];
  /**
   * How this surface is drawn on, spelled out at the foot of the legend. It
   * belongs with the roles because they are read together — somebody opening the
   * (i) is asking "how does this work", not "what is a foot hold" — and the
   * gestures differ by surface: a board is tapped cell by cell, a photo is
   * circled and dragged.
   */
  hint?: string;
}

/**
 * What each colour on the wall means, and how this surface is drawn on.
 *
 * A dialog rather than labels unfolding into a row of buttons, because what a
 * setter new to this needs is a sentence about each role — and that is not
 * something a chip can hold without pushing the row onto three lines for
 * everyone who already knows.
 */
export function HoldRolesLegendDialog({
  open,
  onOpenChange,
  roles,
  hint,
}: HoldRolesLegendDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('climbs.holdRoles.legend.title')}</DialogTitle>
          <DialogDescription>{t('climbs.holdRoles.legend.intro')}</DialogDescription>
        </DialogHeader>

        <ul className="space-y-3">
          {roles.map((role) => {
            const Icon: LucideIcon = PHOTO_HOLD_ROLE_ICONS[role];
            return (
              <li key={role} className="flex items-start gap-3">
                <span
                  className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-border"
                  style={{ color: PHOTO_HOLD_ROLE_SWATCHES[role] }}
                >
                  <Icon className="size-4" aria-hidden />
                </span>
                <span>
                  <span className="block text-sm font-medium">
                    {t(`climbs.holdRoles.${role}.label`)}
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    {t(`climbs.holdRoles.${role}.description`)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>

        {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
      </DialogContent>
    </Dialog>
  );
}
